import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PreviewModal from '../PreviewModal';
import { useUser } from '../../contexts/UserContext';
import './FileManager.css';

function FileManager({ 
  fileType, 
  files, 
  setFiles, 
  canUpload, 
  onFileUpdate,
  selectedMonth,
  selectedYear
}) {
  const { user } = useUser();
  const canProcessFiles = user.role === 'admin' || user.role === 'consultant';
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggingFileId, setDraggingFileId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Hent mapper når komponenten indlæses
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setIsLoading(true);
        const folders = await api.getFolders(fileType, selectedMonth, selectedYear);
        setFolders(folders);
      } catch (err) {
        setUploadError(`Der opstod en fejl ved hentning af mapper: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFolders();
  }, [fileType, selectedMonth, selectedYear]);  

  const handleFileUpload = async (event) => {
    if (!canUpload) {
      setUploadError('Upload er ikke tilladt for lukkede lønkørsler');
      return;
    }

    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploading(true);
      setUploadError(null);
      
      const uploadedFile = await api.upload(
        '/files/upload', 
        file, 
        fileType, 
        selectedMonth,
        selectedYear,
        { 
          folder_id: currentFolder?.id || null,
          existingFiles: files
        }
      );

      setFiles(prev => [...prev, uploadedFile]);
      onFileUpdate?.(uploadedFile);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setUploadError('Mappens navn må ikke være tomt');
      return;
    }

    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const folder = await api.createFolder({
        name: newFolderName,
        type: fileType,
        parent_id: currentFolder?.id || null,
        created_by: user.name,
        month: selectedMonth,
        year: selectedYear
      });

      setFolders(prev => [...prev, folder]);
      setNewFolderName('');
      setIsCreatingFolder(false);
    } catch (err) {
      setUploadError(`Der opstod en fejl ved oprettelse af mappe: ${err.message}`);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await api.deleteFolder(folderId);
      setFolders(prev => prev.filter(folder => folder.id !== folderId));
      if (currentFolder?.id === folderId) {
        setCurrentFolder(null);
      }
    } catch (err) {
      setUploadError(`Der opstod en fejl ved sletning af mappe: ${err.message}`);
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
  };

  const handleBackToRoot = () => {
    setCurrentFolder(null);
  };

  const handleFileDelete = async (fileId) => {
    try {
      setDeletingFileId(fileId);
      await api.delete(`/files/files/${fileId}`);
      setFiles(prev => prev.filter(file => file.id !== fileId));
      onFileUpdate?.({ id: fileId, deleted: true });
    } catch (err) {
      setUploadError(`Der opstod en fejl ved arkivering af fil: ${err.message}`);
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) {
        throw new Error('Fil ikke fundet');
      }

      const response = await api.get(`/files/download/${fileId}`);
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Opdater filens status til 'Downloadet'
      await api.put(`/files/${fileId}/status`, { status: 'Downloadet' });
      
      // Opdater den lokale fil-liste
      setFiles(prev => prev.map(f => 
        f.id === fileId 
          ? { ...f, status: 'Downloadet' }
          : f
      ));

      // Notificer parent komponent om opdateringen
      onFileUpdate?.({ ...file, status: 'Downloadet' });
    } catch (err) {
      setUploadError(`Der opstod en fejl ved download: ${err.message}`);
    }
  };

  const handlePreview = (fileId) => {
    setSelectedFileId(fileId);
    setIsPreviewOpen(true);
  };

  const handleDownloadAll = async (type) => {
    try {
      setIsDownloadingAll(true);
      await api.downloadAll(type, selectedMonth, selectedYear);
      
      // Opdater fil-listen efter download
      const updatedFiles = await api.getFiles(type, selectedMonth, selectedYear);
      setFiles(updatedFiles);
    } catch (err) {
      setUploadError(`Der opstod en fejl ved download af alle filer: ${err.message}`);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Copenhagen'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <i className="fas fa-file-pdf"></i>;
      case 'csv': return <i className="fas fa-file-csv"></i>;
      case 'xlsx':
      case 'xls': return <i className="fas fa-file-excel"></i>;
      default: return <i className="fas fa-file-alt"></i>;
    }
  };

  const handleDragStart = (e, fileId) => {
    setDraggingFileId(fileId);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    setDraggingFileId(null);
    e.currentTarget.classList.remove('dragging');
    setDragOverFolderId(null);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    if (draggingFileId && folderId !== dragOverFolderId) {
      setDragOverFolderId(folderId);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverFolderId(null);
  };

  const handleDrop = async (e, targetFolderId) => {
    e.preventDefault();
    if (!draggingFileId || !targetFolderId) return;

    try {
      setIsLoading(true);
      const draggedFile = files.find(f => f.id === draggingFileId);
      
      // Opdater fil med ny folder_id
      await api.updateFile(draggingFileId, {
        folder_id: targetFolderId
      });

      // Opdater lokalt state
      setFiles(prev => prev.map(file => 
        file.id === draggingFileId 
          ? { ...file, folder_id: targetFolderId }
          : file
      ));

      onFileUpdate?.({ 
        ...draggedFile, 
        folder_id: targetFolderId 
      });

    } catch (err) {
      setUploadError(`Der opstod en fejl ved flytning af fil: ${err.message}`);
    } finally {
      setIsLoading(false);
      setDraggingFileId(null);
      setDragOverFolderId(null);
    }
  };

  const handleLocalFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleLocalFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleLocalFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (!canUpload) {
      setUploadError('Upload er ikke tilladt for lukkede lønkørsler');
      return;
    }

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    try {
      setIsUploading(true);
      setUploadError(null);

      for (const file of droppedFiles) {
        const uploadedFile = await api.upload(
          '/files/upload',
          file,
          fileType,
          selectedMonth,
          selectedYear,
          {
            folder_id: currentFolder?.id || null,
            existingFiles: files
          }
        );

        setFiles(prev => [...prev, uploadedFile]);
        onFileUpdate?.(uploadedFile);
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMarkAsProcessed = async (fileId) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) {
        throw new Error('Fil ikke fundet');
      }

      // Opdater status i backend
      await api.put(`/files/${fileId}/status`, { status: 'Behandlet' });
      
      // Opdater den lokale fil-liste
      setFiles(prev => prev.map(f => 
        f.id === fileId 
          ? { ...f, status: 'Behandlet' }
          : f
      ));

      // Notificer parent komponent om opdateringen
      onFileUpdate?.({ ...file, status: 'Behandlet' });
    } catch (err) {
      setUploadError(`Der opstod en fejl ved markering som behandlet: ${err.message}`);
    }
  };

  return (
    <div className="file-section">
      <div className="file-section-header">
        <div className="folder-navigation">
          {currentFolder && (
            <>
              <button 
                className="back-btn" 
                onClick={handleBackToRoot}
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <span className="current-folder">
                {currentFolder.name}
              </span>
            </>
          )}
        </div>
        <div className="file-actions">
          <button 
            className="create-folder-btn"
            onClick={() => setIsCreatingFolder(true)}
            disabled={isLoading}
          >
            <i className="fas fa-folder-plus"></i>
            Ny mappe
          </button>
          {files.length > 0 && (
            <button 
              className="download-all-btn"
              onClick={() => handleDownloadAll(fileType)}
              data-type={fileType}
              disabled={isLoading || isDownloadingAll}
            >
              <i className={`fas ${isDownloadingAll ? 'fa-spinner fa-spin' : 'fa-file-archive'}`}></i>
              {isDownloadingAll ? 'Downloader...' : 'Download alt'}
            </button>
          )}
          <input
            type="file"
            id={`${fileType}-file-upload`}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            disabled={!canUpload || isLoading}
          />
          <label 
            htmlFor={`${fileType}-file-upload`} 
            className={`upload-btn ${(!canUpload || isLoading) ? 'disabled' : ''}`}
            style={{ 
              opacity: (isUploading || isLoading) ? 0.7 : 1,
              cursor: (!canUpload || isLoading) ? 'not-allowed' : 'pointer',
              backgroundColor: (!canUpload || isLoading) ? '#e9ecef' : undefined,
              color: (!canUpload || isLoading) ? '#6c757d' : undefined
            }}
          >
            <i className="fas fa-cloud-upload-alt"></i>
            {isUploading ? 'Uploader...' : !canUpload ? 'Upload ikke tilladt' : 'Upload fil'}
          </label>
        </div>
      </div>

      {isCreatingFolder && (
        <div className="create-folder-form">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Indtast mappens navn"
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            disabled={isLoading}
          />
          <button onClick={handleCreateFolder} disabled={isLoading}>Opret</button>
          <button onClick={() => setIsCreatingFolder(false)} disabled={isLoading}>Annuller</button>
        </div>
      )}

      {uploadError && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {uploadError}
        </div>
      )}

      <div 
        className={`files-list ${isDraggingOver ? 'dragging-over' : ''}`}
        onDragOver={handleLocalFileDragOver}
        onDragLeave={handleLocalFileDragLeave}
        onDrop={handleLocalFileDrop}
      >
        <div className="files-header">
          <div className="file-column name-column">Navn</div>
          <div className="file-column uploader-column">Uploadet af</div>
          <div className="file-column date-column">Uploadet</div>
          <div className="file-column size-column">Størrelse</div>
          <div className="file-column status-column">Status</div>
          <div className="file-column actions-column">Handlinger</div>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            Indlæser...
          </div>
        ) : (
          <>
            {/* Vis mapper først */}
            {folders
              .filter(folder => folder.parent_id === (currentFolder?.id || null))
              .map(folder => (
                <div 
                  key={folder.id} 
                  className={`file-item folder-item ${dragOverFolderId === folder.id ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, folder.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, folder.id)}
                >
                  <div className="file-column name-column" onClick={() => handleFolderClick(folder)}>
                    <i className="fas fa-folder"></i>
                    {folder.name}
                  </div>
                  <div className="file-column uploader-column"></div>
                  <div className="file-column date-column">{formatDate(folder.created_at)}</div>
                  <div className="file-column size-column">-</div>
                  <div className="file-column status-column">-</div>
                  <div className="file-column actions-column">
                    <button 
                      className="action-btn delete-btn" 
                      title="Slet mappe"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      disabled={isLoading}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}

            {/* Vis filer efter mapper */}
            {files
              .filter(file => file.folder_id === (currentFolder?.id || null))
              .map(file => (
                <div 
                  key={file.id} 
                  className="file-item"
                  draggable={!isLoading}
                  onDragStart={(e) => handleDragStart(e, file.id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="file-column name-column">
                    {getFileIcon(file.name)}
                    {file.name}
                  </div>
                  <div className="file-column uploader-column">{file.uploaded_by || '-'}</div>
                  <div className="file-column date-column">{formatDate(file.created_at)}</div>
                  <div className="file-column size-column">{formatFileSize(file.size)}</div>
                  <div className="file-column status-column">
                    <span className={`status-badge ${file.status.toLowerCase()}`}>
                      {file.status}
                    </span>
                  </div>
                  <div className="file-column actions-column">
                    <button 
                      className="action-btn preview-btn" 
                      title="Forhåndsvis"
                      onClick={() => handlePreview(file.id)}
                      disabled={isLoading}
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    {fileType === 'input' && file.status !== 'Behandlet' && canProcessFiles && (
                      <button 
                        className="action-btn process-btn" 
                        title="Marker som behandlet"
                        onClick={() => handleMarkAsProcessed(file.id)}
                        disabled={isLoading}
                      >
                        <i className="fas fa-check-circle"></i>
                      </button>
                    )}
                    <button 
                      className="action-btn download-btn" 
                      title="Download"
                      onClick={() => handleDownload(file.id)}
                      disabled={isLoading}
                    >
                      <i className="fas fa-download"></i>
                    </button>
                    <button 
                      className="action-btn delete-btn" 
                      title="Slet"
                      onClick={() => handleFileDelete(file.id)}
                      disabled={deletingFileId === file.id || isLoading}
                    >
                      {deletingFileId === file.id ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-trash"></i>
                      )}
                    </button>
                  </div>
                </div>
              ))}

            {folders.filter(folder => folder.parent_id === (currentFolder?.id || null)).length === 0 && 
             files.filter(file => file.folder_id === (currentFolder?.id || null)).length === 0 && (
              <div className="no-files">
                <i className="fas fa-inbox"></i>
                Ingen filer eller mapper endnu
              </div>
            )}
          </>
        )}
      </div>

      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setSelectedFileId(null);
        }}
        fileId={selectedFileId}
      />
    </div>
  );
}

export default FileManager; 